import React, { useMemo, useState, useRef } from 'react';
import { Title, useUpdate, useNotify, useDataProvider, useStore } from 'react-admin';
import type { ColDef } from 'ag-grid-community';
import { ActionColumn } from '../../components/ag-grid/aggrid-components';
import AgGrid from '../../components/ag-grid/ag-grid';
import { autoLoad } from '../../utils/common';
import RenderControls from '../../components/ra-list/RenderControls';
import { formatDateTime,formatLocalTime } from '../../components/ex_dayjs';
import NumericEditor from '../../components/controls/numeric-editor';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ListingStatus } from './types';
import type { AdditionalListing, FilterControl } from './types';
import ExtensionSetup, { useExtensionDetected } from './ExtensionSetup';
import MasterView from './master-view';

const LISTING_STATUS: Record<number, { label: string; color: string }> = {
    [ListingStatus.NEW]: { label: 'New', color: 'blue' },
    [ListingStatus.ACTIVE]: { label: 'Active', color: 'green' },
    [ListingStatus.EXCLUDED]: { label: 'Excluded', color: 'orange' },
    [ListingStatus.UNAVAILABLE]: { label: 'Unavailable', color: 'red' },
    [ListingStatus.PAUSED]: { label: 'Paused', color: 'grey' },
};

const AdditionalListingsView: React.FC = () => {
    const refInput = useRef<any>(null);
    const refbtnFilter = useRef<HTMLElement>(null);
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [handleUpdate] = useUpdate();
    const [defaultValues, setDefaultValues] = useState({
        sortField: { field: 'id', order: 'desc' },
    });
    const extensionStatus = useExtensionDetected();
    const [extensionDismissed, setExtensionDismissed] = useState(false);
    const [excludeSiteIdOpen, setExcludeSiteIdOpen] = useState(false);
    const [excludeSiteIdValue, setExcludeSiteIdValue] = useState('');
    const [view, setView] = useStore<'list' | 'master'>('additionalListings.view', 'list');

    const handleBulkSetStatus = (status: number): void => {
        const selectedRows = refInput.current?.getAgGridInstance()?.api?.getSelectedRows();
        if (!selectedRows || selectedRows.length === 0) {
            notify('No rows selected', { type: 'warning', autoHideDuration: 2000 });
            return;
        }
        const ids = selectedRows.map((row: AdditionalListing) => row.id);
        const statusLabel = LISTING_STATUS[status]?.label ?? String(status);
        dataProvider.updateMany('AdditionalListings', { ids, data: { status } })
            .then(() => {
                notify(`${ids.length} listing(s) set to ${statusLabel}`, { type: 'success', autoHideDuration: 2000 });
                autoLoad(refbtnFilter);
            })
            .catch((error: any) => {
                notify(`Update error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
            });
    };

    const handleExcludeBySiteId = (): void => {
        const siteId = excludeSiteIdValue.trim();
        if (!siteId) {
            notify('Please enter a Site ID', { type: 'warning', autoHideDuration: 2000 });
            return;
        }
        dataProvider.postData('AdditionalListings', 'ExcludeBySiteId', { siteId })
            .then((res: any) => {
                notify(res.data?.message ?? `Listings excluded for siteId '${siteId}'`, { type: 'success', autoHideDuration: 3000 });
                setExcludeSiteIdOpen(false);
                setExcludeSiteIdValue('');
                autoLoad(refbtnFilter);
            })
            .catch((error: any) => {
                notify(`Exclude error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
            });
    };

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 80,
        filter: false,
        suppressMovable: true
    }), []);

    const onCellClicked = (params: any): void => {
        if (params.column.colId !== 'action' || !params.event.target.dataset.action) return;
        const action = params.event.target.dataset.action;

        if (action === 'edit') {
            params.api.startEditingCell({
                rowIndex: params.node.rowIndex,
                colKey: params.columnApi.getDisplayedCenterColumns()[0].colId
            });
        }
        if (action === 'update') {
            params.api.stopEditing(false);
            handleUpdate('AdditionalListings', { id: params.data.id, data: params.data }, {
                onSuccess: () => {
                    notify('Update successfully', { type: 'success', autoHideDuration: 2000 });
                },
                onError: (error: any) => {
                    notify(`Update error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
                },
            });
            params.api.stopEditing();
        }
        if (action === 'cancel') {
            params.api.stopEditing(true);
        }
    };

    const onCellEditingStopped = (params: any): void => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };

    const onCellEditingStarted = (params: any): void => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };

    const colDefs: ColDef[] = [
        { headerCheckboxSelection: true, checkboxSelection: true, maxWidth: 50, sortable: false, resizable: false, flex: 0 },
        { field: 'id', headerName: 'ID', maxWidth: 130 },
        { field: 'eventId', headerName: 'Event ID', maxWidth: 120 },
        { field: 'eventSiteId', headerName: 'Site ID', maxWidth: 120 },
        {
            field: 'eventName', headerName: 'Event', minWidth: 200,
            cellRenderer: (params: any) => {
                if (!params.value) return '';
                if (!params.data?.eventUrl) return params.value;
                return <a href={params.data.eventUrl} target="_blank" rel="noopener noreferrer">{params.value}</a>;
            }
        },
        { field: 'eventVenue', headerName: 'Venue', minWidth: 150 },
        {
            field: 'eventDateTime', headerName: 'Event Date', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? formatDateTime(params.value) : ''
        },
        { field: 'section', headerName: 'Section', editable: true },
        { field: 'row', headerName: 'Row', editable: true, maxWidth: 90 },
        { field: 'seats', headerName: 'Seats', editable: true },
        {
            field: 'price', headerName: 'Price', editable: true, maxWidth: 110,
            cellEditor: NumericEditor,
            valueParser: (params: any) => Number(params.newValue),
            valueFormatter: (params: any) => params.value !== null && params.value !== undefined ? `$${Number(params.value).toFixed(2)}` : ''
        },
        { field: 'priceLevel', headerName: 'Price Level', editable: true },
        {
            field: 'numTix', headerName: 'NumTix', editable: true, maxWidth: 90,
            cellEditor: NumericEditor,
            valueParser: (params: any) => Number(params.newValue)
        },
        { field: 'type', headerName: 'Type', maxWidth: 100 },
        { field: 'exact', headerName: 'Exact', maxWidth: 70 },
        {
            field: 'status', headerName: 'Status', maxWidth: 110,
            valueFormatter: (params: any) => LISTING_STATUS[params.value]?.label ?? String(params.value),
            cellStyle: (params: any) => ({ color: LISTING_STATUS[params.value]?.color ?? 'inherit' })
        },
        {
            field: 'insertDate', headerName: 'Created', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? formatLocalTime(params.value) : ''
        },
        {
            field: 'modifiedDate', headerName: 'Modified', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? formatLocalTime(params.value) : ''
        },
        { field: 'modifiedBy', headerName: 'Modified By', maxWidth: 160 },
        {
            headerName: 'Action', minWidth: 150,
            cellRenderer: ActionColumn,
            colId: 'action'
        }
    ];

    const controls: FilterControl[] = [
        { component: 'number', name: 'eventId_eq', label: 'Event ID', xs: 0.8 },
        { component: 'text', name: 'eventSiteId', label: 'Site ID', xs: 1 },
        { component: 'text', name: 'eventName', label: 'Event Name', xs: 1.5 },
        { component: 'text', name: 'eventVenue', label: 'Venue', xs: 1.5 },
        // { component: 'daterange', name: 'eventDateTime', label: 'Event Date', xs: 2, isUTC: true },
        { component: 'text', name: 'section', label: 'Section', xs: 1 },
        { component: 'text', name: 'row', label: 'Row', xs: 0.7 },
        { component: 'select', name: 'status_eq', label: 'Status', xs: 1, choices: [{ id: ListingStatus.NEW, name: 'New' }, { id: ListingStatus.ACTIVE, name: 'Active' }, { id: ListingStatus.EXCLUDED, name: 'Excluded' }, { id: ListingStatus.UNAVAILABLE, name: 'Unavailable' }] },
    ];

    const handleSearch = (filterList: Record<string, unknown>): void => {
        refInput.current.customFilterSearch(filterList);
    };

    return (
        <div>
            <Title title={'Additional Listings'} />
            {!extensionDismissed && (extensionStatus.detected === false || extensionStatus.outdated) && <ExtensionSetup outdated={extensionStatus.outdated} version={extensionStatus.version} onClose={() => setExtensionDismissed(true)} />}
            <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_e, val) => { if (val) setView(val); }}
                size="small"
                sx={{ mb: 1 }}
            >
                <ToggleButton value="list">List</ToggleButton>
                <ToggleButton value="master">Master / Details</ToggleButton>
            </ToggleButtonGroup>
            {view === 'master' ? (
                <MasterView />
            ) : (
                <>
                    <RenderControls
                        controls={controls}
                        onSearch={handleSearch}
                        refFilter={refbtnFilter}
                        filterInline={true} // if there are many filters, it's better to set false
                        agSource='AdditionalListings'
                        agSort={defaultValues.sortField}
                        exportFile={{
                            filename: `AdditionalListings-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                            columns: ['id', 'eventId', 'eventSiteId', 'eventName', 'eventVenue', 'eventDateTime', 'eventUrl', 'section', 'row', 'seats', 'price', 'priceLevel', 'numTix', 'type', 'exact', 'status', 'insertDate', 'modifiedDate', 'modifiedBy'],
                            limit: 1000000
                        }}
                        actions={
                            <>
                                <Button variant="contained" color="success" size="small" onClick={() => handleBulkSetStatus(ListingStatus.ACTIVE)}>
                                    Set Active
                                </Button>
                                <Button variant="contained" color="warning" size="small" onClick={() => handleBulkSetStatus(ListingStatus.EXCLUDED)}>
                                    Set Excluded
                                </Button>
                                <Button variant="contained" size="small" sx={{ bgcolor: 'grey.500', '&:hover': { bgcolor: 'grey.700' } }} onClick={() => handleBulkSetStatus(ListingStatus.PAUSED)}>
                                    Set Paused
                                </Button>
                                {/* <Button variant="outlined" color="error" size="small" onClick={() => setExcludeSiteIdOpen(true)}>
                                    Exclude by Site ID
                                </Button> */}
                            </>
                        }
                    />
                    <AgGrid
                        ref={refInput}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        setDefaultValues={setDefaultValues}
                        rowHeight={45}
                        rowSelection='multiple'
                        suppressRowClickSelection={true}
                        editType={'fullRow'}
                        onCellClicked={onCellClicked}
                        onCellEditingStopped={onCellEditingStopped}
                        onCellEditingStarted={onCellEditingStarted}
                        list={'AdditionalListings'}
                    />
                    <Dialog open={excludeSiteIdOpen} onClose={() => setExcludeSiteIdOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Exclude Listings by Site ID</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Site ID"
                                fullWidth
                                value={excludeSiteIdValue}
                                onChange={(e) => setExcludeSiteIdValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleExcludeBySiteId(); }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setExcludeSiteIdOpen(false)}>Cancel</Button>
                            <Button variant="contained" color="warning" onClick={handleExcludeBySiteId}>Exclude</Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </div>
    );
};

export default AdditionalListingsView;
